import { Link } from 'react-router-dom';
import Reveal from '@/components/base/Reveal';
import Counter from '@/components/base/Counter';
import SectionHeading from '@/components/base/SectionHeading';
import { stats } from '@/mocks/site';

const highlights = [
  "Atelier intégré à Logbaba : découpe, roulage, assemblage et contrôle",
  "Soudeurs qualifiés TIG, MIG/MAG et ARC sur acier et inox",
  "Interventions en atelier comme sur site, au Cameroun et dans la sous-région",
];

export default function Intro() {
  return (
    <section id="entreprise" className="relative overflow-hidden bg-background-100 pb-0 pt-20 md:pt-28">
      <div className="pointer-events-none absolute inset-0 paper-grid opacity-70" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-foreground-950/10" />

      <div className="container-wide relative">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_1fr] lg:gap-20">
          <Reveal variant="fade" className="relative">
            <div className="relative">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-background-300">
                <img
                  src="https://readdy.ai/api/search-image?query=Industrial%20welder%20in%20navy%20blue%20protective%20jacket%20and%20helmet%20welding%20a%20thick%20steel%20pressure%20vessel%20inside%20a%20large%20metal%20workshop%2C%20warm%20orange%20sparks%20flying%2C%20cool%20blue%20daylight%20from%20high%20windows%2C%20realistic%20documentary%20industrial%20photography%2C%20rich%20textures%20of%20steel%20and%20soot%2C%20no%20visible%20logo&width=1200&height=900&seq=sgsctic-atelier-portrait-2026&orientation=landscape"
                  alt="Soudeur de l'atelier SGSCTIC en intervention sur un ouvrage chaudronné"
                  title="Atelier SGSCTIC — chaudronnerie et soudure à Douala"
                  className="h-full w-full object-cover object-top"
                />
                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-foreground-950/15" />
              </div>
              <div className="absolute -bottom-10 -right-2 hidden w-[46%] overflow-hidden rounded-lg border-4 border-background-100 bg-background-300 sm:block lg:-right-10">
                <div className="aspect-[4/5] w-full">
                  <img
                    src="https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.39.37-1.jpeg"
                    alt="Soudeur SGSCTIC en intervention dans l'atelier"
                    title="Soudure industrielle à Douala"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="absolute -left-2 top-6 flex items-center gap-3 bg-primary-950 px-5 py-4 lg:-left-6">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-accent-500/50">
                  <i className="ri-map-pin-line text-accent-400" />
                </span>
                <div className="leading-tight">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-background-400">
                    Atelier &amp; siège
                  </p>
                  <p className="font-heading text-lg uppercase tracking-wide text-background-50">
                    Logbaba, Douala
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <SectionHeading
                label="Qui sommes-nous"
                title={
                  <>
                    Un partenaire industriel,
                    <br />
                    pas un simple prestataire
                  </>
                }
              />
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-7 text-base leading-relaxed text-foreground-700">
                Forte de son expérience, SGSCTIC s'impose comme un acteur des travaux industriels
                lourds au Cameroun. Nous accompagnons les industries locales et les multinationales
                avec des solutions fiables, pensées pour durer et adaptées aux contraintes réelles
                du terrain.
              </p>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">
                De la fabrication d'une cuve à la mise en service d'un réseau de tuyauterie, chaque
                projet est piloté par une équipe technique qui parle le même langage que vos
                ingénieurs.
              </p>
            </Reveal>

            <div className="mt-8 space-y-4">
              {highlights.map((item, index) => (
                <Reveal key={item} delay={180 + index * 90}>
                  <div className="flex items-start gap-4 border-b border-foreground-950/10 pb-4">
                    <span className="mt-2 tick-square" />
                    <p className="text-sm leading-relaxed text-foreground-800">{item}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={420}>
              <Link
                to="/a-propos"
                className="group mt-9 inline-flex cursor-pointer items-center gap-3 text-sm font-semibold uppercase tracking-[0.14em] text-foreground-950"
              >
                <span className="link-underline">Notre histoire</span>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-1">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </div>

      {/* bandeau chiffres — ancrage sombre */}
      <div className="relative mt-20 bg-primary-950 md:mt-24">
        <div className="pointer-events-none absolute inset-0 paper-grid-dark opacity-60" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-accent-500" />
        <div className="container-wide relative">
          <div className="grid grid-cols-2 gap-px bg-background-50/10 lg:grid-cols-4">
            {stats.map((stat, index) => (
              <Reveal key={stat.label} delay={index * 100} className="bg-primary-950">
                <div className="h-full px-5 py-9 md:px-8 md:py-11">
                  <div className="flex items-baseline gap-1">
                    <Counter
                      value={stat.value}
                      suffix={stat.suffix}
                      className="font-heading text-5xl font-semibold leading-none text-accent-400 md:text-6xl"
                    />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-background-50">{stat.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-background-400">{stat.hint}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}